import { useState } from "react";
import type { Health } from "../types";

type Level = "blocker" | "warning";

interface Issue {
  label: string;
  detail: string;
  level: Level;
}

interface Props {
  health: Health | null;
  /** Set when /api/health itself could not be read. */
  error?: string | null;
}

/**
 * What is wrong with the configuration, if anything.
 *
 * Every one of these fails late and unhelpfully when missing - a migration
 * that runs for a minute before reporting "no API key" wastes the minute - so
 * they are worth surfacing before the button is pressed. What is *right* is
 * not worth a permanent row of green badges: a header that always reports
 * success trains people to stop reading it, and it is the first thing on
 * screen in a demo where none of it is the point.
 */
function issuesFor(health: Health | null, error: string | null): Issue[] {
  if (error) {
    return [
      {
        label: "Backend unreachable",
        detail: `The server could not be asked about its configuration. ${error}`,
        level: "blocker",
      },
    ];
  }

  // Still loading. Nothing is known yet, so nothing is wrong yet.
  if (!health) return [];

  const issues: Issue[] = [];

  if (!health.lexRegion) {
    issues.push({
      label: "No Lex region",
      detail: "Set LEX_REGION or AWS_REGION, or no bot can be read.",
      level: "blocker",
    });
  }
  if (!health.workspaceId) {
    issues.push({
      label: "No CX Designer workspace",
      detail: "Set ACXD_WORKSPACE_ID to say where applications are created.",
      level: "blocker",
    });
  }
  if (!health.acxdKeyConfigured) {
    issues.push({
      label: "No CX Designer API key",
      detail: "Set ACXD_API_KEY. Nothing can be created without it.",
      level: "blocker",
    });
  }
  if (!health.artifactBucket) {
    // Not fatal, but worth knowing: on Lambda the local disk is thrown away
    // with the container, so the record of the migration goes with it.
    issues.push({
      label: "Artifacts stored locally",
      detail:
        "No ARTIFACT_BUCKET, so flow definitions are written to disk rather " +
        "than S3 and will not survive a serverless deployment.",
      level: "warning",
    });
  }

  return issues;
}

export function HealthBar({ health, error }: Props) {
  const [open, setOpen] = useState(false);
  const issues = issuesFor(health, error ?? null);

  // A clean configuration says nothing at all.
  if (!issues.length) return null;

  const blocking = issues.some((issue) => issue.level === "blocker");
  const summary = `${issues.length} configuration ${
    issues.length === 1 ? "issue" : "issues"
  }`;

  return (
    <div className="alert-slot">
      <button
        type="button"
        className={`alert-btn ${blocking ? "blocker" : "warning"}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={summary}
        title={summary}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path
            d="M12 3.5 1.8 20.5h20.4L12 3.5Z M12 10v4.4 M12 17.4v.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="alert-count">{issues.length}</span>
      </button>

      {open && (
        <div className="toast" role="alert">
          <div className="toast-head">
            <strong>{summary}</strong>
            <button
              type="button"
              className="toast-close"
              onClick={() => setOpen(false)}
              aria-label="Dismiss"
            >
              {"\u00d7"}
            </button>
          </div>
          <ul className="toast-list">
            {issues.map((issue) => (
              <li key={issue.label} className={issue.level}>
                <span className="toast-label">{issue.label}</span>
                <span className="toast-detail">{issue.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
